import { Box } from '@cp/ds/src/components/box';
import { Container } from '@cp/ds/src/components/container';
import { Card } from '@cp/ds/src/components/card';
import { Stack } from '@cp/ds/src/components/stack';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import React from 'react';
import { alpha } from '@mui/system/colorManipulator';
import Grow from '@mui/material/Grow';
import { Typography } from '@cp/ds/src/components/typography';
import { Breadcrumbs } from '@cp/ds/src/components/breadcrumbs';
import { SxProps } from '@cp/ds';
import { coreCareerApi } from '@cp/entities/career';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useSelector } from 'react-redux';
import { LocationFieldset } from '@cp/entities/location';
import { LocationIcon } from '@cp/ds/src/icons';
import { RoutedLink as Link } from 'src/shared/ui/link';
import Header from 'src/widgets/header';
import { H1 } from 'src/app/shared/components/h1';
import { useH1 } from 'src/app/shared/hooks/useH1';
import { catalogLayoutDesktopViewModeSelector } from 'src/features/catalog';
import {
  CATALOG_DESKTOP_COLUMN_WIDTH_FIXED,
  CatalogBannerSearchForVacancy,
  CatalogList,
  // YOU-5106: CatalogLocationLinking,
  VacancyCardWidget,
  useCatalogBreadcrumbs,
} from 'src/widgets/catalog';
import { CitySelection } from 'src/widgets/location';
import { CatalogBannerHowToFindJob } from 'src/entities/catalog/banner/howToFindJob';
import { useCatalogHeaderConfig } from 'src/app/routes/catalog/hooks/useCatalogHeaderConfig';
// YOU-5106: import { CatalogCareerTagClouds } from 'src/widgets/catalogCareerTagClouds';
import { CatalogNav } from 'src/entities/catalog/nav';
import { PrimaryHeaderBannerV2 } from 'src/widgets/primaryHeaderBanner';
import { CatalogFilter } from 'src/entities/catalog/filter';
import { CatalogBannerSocialRanking } from 'src/entities/catalog/banner';
import { CatalogViewProps } from './types';
import { LazyCatalogMap } from './lib';
import { CATALOG_DESKTOP_CONTAINER_MAX_WIDTH, CATALOG_VACANCY_SUBTITLE } from './const';

const CATALOG_DESKTOP_COLUMN_SPACING = 2;
const FILTER_FIRST_SX: SxProps = { mb: CATALOG_DESKTOP_COLUMN_SPACING };

const CatalogVacancyDesktopView: React.FC<CatalogViewProps> = ({
  currentCity,
  pageProps,
  listRequest,
  onChangeCity,
  onChangeViewMode,
  onChangeSorting,
  onChangeFilter,
  sorting,
  filter,
}) => {
  const { careerId } = pageProps;
  const theme = useTheme();
  const h1RenderConfig = useH1();
  const viewMode = useSelector(catalogLayoutDesktopViewModeSelector);
  const { data: careerDictionary } = coreCareerApi.endpoints.getCareers.useQuery();
  const loggedIn = useIsLoggedIn();
  const breadcrumbs = useCatalogBreadcrumbs();

  const renderSection = ({
    content,
    filled,
    title,
    href,
  }: {
    href?: string;
    filled?: boolean;
    content: React.ReactNode;
    title: React.ReactNode;
  }) => {
    const header = (
      <Typography component="div" sx={{ textAlign: 'center', mb: { xs: 2.5, md: 5 }, fontSize: { xs: '24px', md: '30px' } }} variant="h1">
        {title}
      </Typography>
    );

    return (
      <Stack
        sx={{
          alignItems: 'center',
          ...(filled && {
            px: 3.75,
            pt: 1.5,
            pb: 3.75,
            borderRadius: `${theme.borderRadius.extraLarge}px`,
            backgroundColor: alpha(theme.palette['gray middle'], 0.2),
          }),
        }}
      >
        <Box sx={{ border: '1px solid grey', width: '73px', mb: '16px' }} />
        {href ? (
          <Link external href={href} sx={{ mb: 5 }} underline={false}>
            {header}
          </Link>
        ) : (
          header
        )}
        {content}
      </Stack>
    );
  };

  const containerMaxWidth = loggedIn ? undefined : CATALOG_DESKTOP_CONTAINER_MAX_WIDTH;

  return (
    <Box component="main" sx={{ backgroundColor: 'background.paper', overflow: 'hidden' }}>
      <Header
        {...useCatalogHeaderConfig()}
        containerProps={{ maxWidth: containerMaxWidth }}
        sx={{
          boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.1)',
          zIndex: 5,
          ...(viewMode === 'map'
            ? { position: 'fixed', left: 0, top: 0, right: 0, width: 1 }
            : {
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
              }),
          backgroundColor: theme.palette.white,
        }}
      />

      <Container maxWidth={containerMaxWidth}>
        <Box sx={{ overflow: 'hidden', my: 2.5 }}>
          <PrimaryHeaderBannerV2
            content={
              <H1
                config={h1RenderConfig}
                itemProps={{ bold: { variant: 'h2' } }}
                rootProps={{
                  sx: {
                    ml: 0,
                    mr: 'auto',
                    mt: 1,
                    mb: 0,
                    transition: theme.transitions.create(['max-height'], {
                      duration: theme.transitions.duration.standard,
                    }),
                    overflow: 'hidden',
                  },
                  variant: 'h2',
                }}
              />
            }
            hasOffset
            header={<Breadcrumbs LinkComponent={Link} items={breadcrumbs} sx={{ alignSelf: 'flex-start' }} />}
            loggedIn={loggedIn}
            pageProps={pageProps}
            subtitleLight={loggedIn ? '' : CATALOG_VACANCY_SUBTITLE}
          />
        </Box>

        <Stack alignItems="flex-start" direction="row">
          <Stack
            spacing={CATALOG_DESKTOP_COLUMN_SPACING}
            sx={{ width: CATALOG_DESKTOP_COLUMN_WIDTH_FIXED, marginRight: CATALOG_DESKTOP_COLUMN_SPACING }}
          >
            <Box
              sx={{
                flexShrink: 0,
                backgroundColor: 'white',
                p: 1.5,
                borderRadius: '16px',
                boxShadow: '14px 17px 40px 4px rgba(138, 182, 209, 0.08)',
              }}
            >
              <CatalogNav
                careerDictionary={careerDictionary}
                careerId={pageProps.careerId}
                catalogType={pageProps.catalogType}
                citySearchFormField={
                  <LocationFieldset
                    // endAdornment={<LocationIcon fontSize="1rem" />}
                    placeholder="Город, адрес"
                    size="small"
                    sx={{ zIndex: 5, width: '100%' }}
                  />
                }
                defaultCity={currentCity}
                defaultFilter={filter}
                defaultSorting={sorting}
                onChange={onChangeFilter}
                onChangeCity={onChangeCity}
                onChangeSorting={onChangeSorting}
                sx={FILTER_FIRST_SX}
              />
              <CatalogFilter
                careerDictionary={careerDictionary}
                careerId={pageProps.careerId}
                catalogType={pageProps.catalogType}
                defaultFilter={filter}
                onChange={onChangeFilter}
                onMapButtonClick={viewMode !== 'map' ? () => onChangeViewMode('map') : undefined}
              />
            </Box>
            <CatalogBannerSocialRanking />
          </Stack>

          <Stack direction="column" sx={{ flex: 1 }}>
            <Stack alignItems="center">
              <Grow appear={false} in={viewMode === 'map'} unmountOnExit>
                <Box
                  sx={{
                    width: '100%',
                    height: 480,
                    overflow: 'hidden',
                    borderRadius: '20px',
                    mb: CATALOG_DESKTOP_COLUMN_SPACING,
                  }}
                >
                  <LazyCatalogMap
                    listRequest={listRequest}
                    onClose={() => onChangeViewMode('list')}
                    sx={{ width: 1, height: 1, flexGrow: 1, position: 'relative', top: 0, left: 0, right: 0, bottom: 0 }}
                    zoomControl
                  />
                </Box>
              </Grow>

              <CatalogList
                CatalogItem={VacancyCardWidget}
                banners={{
                  3: loggedIn ? undefined : (
                    <Card
                      sx={{
                        overflow: 'hidden',
                        borderRadius: '24px',
                        flexGrow: 1,
                        border: `1px solid ${theme.palette.gray}`,
                        boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
                      }}
                    >
                      <CatalogBannerSearchForVacancy careerDict={careerDictionary} careerId={pageProps.careerId} />
                    </Card>
                  ),
                }}
                itemSpacing={CATALOG_DESKTOP_COLUMN_SPACING}
                itemWidth="100%"
                request={listRequest}
              />

              {!loggedIn && (
                <Box sx={{ borderRadius: '24px', overflow: 'hidden', my: { xs: '30px', md: '80px' } }}>
                  <CatalogBannerHowToFindJob careerDict={careerDictionary} careerId={careerId} />
                </Box>
              )}

              {/* YOU-5106: restore when other specialties are enabled
              {!loggedIn && (
                <Stack direction="row" sx={loggedIn ? undefined : { width: 1, mx: 'auto' }}>
                  {renderSection({
                    title: 'Вас может заинтересовать',
                    content: (
                      <Stack
                        direction="row"
                        spacing={2.5}
                        sx={{
                          alignItems: 'flex-start',
                          width: '100%',
                          pb: 10,
                          '& > *': { flexBasis: 'calc(50% - 10px)' },
                          flexWrap: 'wrap',
                        }}
                      >
                        <CatalogCareerTagClouds />
                        <CatalogLocationLinking sx={{ '#root &': { mt: 2.5, ml: 0 } }} />
                      </Stack>
                    ),
                  })}
                </Stack>
              )}
              */}
            </Stack>
          </Stack>
        </Stack>
      </Container>

      <CitySelection />
    </Box>
  );
};

export default React.memo(CatalogVacancyDesktopView);
